// Portal homepage — Jira Service Management catalog style. Portal Groups are
// the primary navigation: each renders as a large image card. Clicking a
// group drills into its request types. Request types not assigned to a group
// fall into an "Other requests" bucket so admins never lose them.
// A search bar at the top flattens the catalog to matching request types.
import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CaretRight, MagnifyingGlass, FolderSimple, BookOpen, X, ThumbsUp, ThumbsDown } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { usePortal } from './PortalLayout';
import PortalRtCard from './PortalRtCard';
import { RichEditorContent } from '../../components/RichEditor';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

export default function PortalHomePage() {
  const { portal, groups, request_types, slug } = usePortal();
  const theme = portal.branding?.theme_color || '#2563eb';
  const layout = portal.catalog_layout || 'image_cards';
  const [q, setQ] = useState('');
  const [articles, setArticles] = useState([]);
  const [openArticle, setOpenArticle] = useState(null);

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/public/portal/${slug}/kb`).then(r => setArticles(r.data || [])).catch(() => {});
  }, [slug]);

  const needle = q.trim().toLowerCase();

  const kbResults = useMemo(() => {
    if (!needle) return [];
    return articles.filter(a => `${a.title} ${a.excerpt || ''} ${(a.tags || []).join(' ')}`.toLowerCase().includes(needle));
  }, [needle, articles]);

  const searchResults = useMemo(() => {
    if (!needle) return [];
    return request_types.filter(rt =>
      (rt.name || '').toLowerCase().includes(needle) ||
      (rt.description || '').toLowerCase().includes(needle)
    );
  }, [needle, request_types]);

  const groupsWithCounts = useMemo(() => groups.map(g => ({
    ...g,
    count: request_types.filter(rt => rt.portal_group_id === g.group_id).length,
  })), [groups, request_types]);

  const unassigned = useMemo(
    () => request_types.filter(rt => !rt.portal_group_id),
    [request_types]
  );

  const isEmpty = groups.length === 0 && unassigned.length === 0;

  return (
    <div className="space-y-8" data-testid="portal-home">
      {/* Search bar — sits above the catalog so customers can pivot quickly. */}
      <div className="relative max-w-xl">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search for a request type…" className="pl-9 bg-white"
          data-testid="portal-search-input" />
      </div>

      {needle ? (
        (searchResults.length === 0 && kbResults.length === 0) ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
            <p className="text-sm text-zinc-500">Nothing matches "{q}".</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="portal-search-results">
            {searchResults.length > 0 && (
              <section>
                <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">Request types</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {searchResults.map(rt => (
                    <PortalRtCard key={rt.request_type_id} rt={rt} slug={slug} theme={theme} />
                  ))}
                </div>
              </section>
            )}
            {kbResults.length > 0 && (
              <section data-testid="portal-kb-results">
                <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3 flex items-center gap-1.5"><BookOpen size={15} /> Knowledge base</h2>
                <div className="space-y-2">
                  {kbResults.map(a => <ArticleRow key={a.article_id} a={a} onOpen={() => setOpenArticle(a)} />)}
                </div>
              </section>
            )}
          </div>
        )
      ) : isEmpty ? (
        <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
          <p className="text-sm text-zinc-500">This portal hasn't been configured yet.</p>
        </div>
      ) : (
        <>
          {groups.length > 0 && (
            <section data-testid="portal-groups-grid">
              <div className={
                layout === 'list'
                  ? 'space-y-3'
                  : layout === 'compact'
                    ? 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4'
                    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5'
              }>
                {groupsWithCounts.map(g => (
                  <PortalGroupCard key={g.group_id} group={g} slug={slug} theme={theme} layout={layout} />
                ))}
              </div>
            </section>
          )}

          {unassigned.length > 0 && (
            <section data-testid="portal-group-other">
              {groups.length > 0 && (
                <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3">Other requests</h2>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {unassigned.map(rt => (
                  <PortalRtCard key={rt.request_type_id} rt={rt} slug={slug} theme={theme} />
                ))}
              </div>
            </section>
          )}
          {articles.length > 0 && (
            <section data-testid="portal-kb-section">
              <h2 className="text-sm font-bold text-zinc-900 uppercase tracking-wider mb-3 flex items-center gap-1.5"><BookOpen size={15} style={{ color: theme }} /> Popular articles</h2>
              <div className="space-y-2">
                {articles.slice(0, 6).map(a => <ArticleRow key={a.article_id} a={a} onOpen={() => setOpenArticle(a)} />)}
              </div>
            </section>
          )}
        </>
      )}

      {openArticle && <ArticleModal article={openArticle} theme={theme} onClose={() => setOpenArticle(null)} />}
    </div>
  );
}

function ArticleRow({ a, onOpen }) {
  return (
    <button onClick={onOpen} data-testid={`portal-kb-item-${a.article_id}`}
      className="w-full text-left flex items-center gap-3 bg-white border border-zinc-200 rounded-lg px-4 py-3 hover:shadow-sm hover:border-zinc-300 transition-all">
      <BookOpen size={16} className="text-zinc-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-zinc-800 truncate">{a.title}</p>
        {a.excerpt && <p className="text-xs text-zinc-400 truncate">{a.excerpt}</p>}
      </div>
      <CaretRight size={14} className="text-zinc-300 shrink-0" />
    </button>
  );
}

function ArticleModal({ article, theme, onClose }) {
  const [full, setFull] = useState(null);
  const [voted, setVoted] = useState(false);
  useEffect(() => {
    axios.get(`${API}/public/kb/${article.article_id}`).then(r => setFull(r.data)).catch(() => setFull({ ...article, body: '<em>Unable to load.</em>' }));
  }, [article.article_id]);
  const vote = (helpful) => { if (voted) return; setVoted(true); axios.post(`${API}/public/kb/${article.article_id}/vote`, { helpful }).catch(() => {}); };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="portal-kb-modal">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white h-full overflow-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-zinc-100 px-6 py-4 flex items-start justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: theme }}>{article.category}</p>
            <h2 className="text-lg font-bold text-zinc-900">{article.title}</h2>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          {full == null ? <p className="text-sm text-zinc-400">Loading…</p> : <div className="prose prose-sm max-w-none"><RichEditorContent html={full.body} /></div>}
          <div className="mt-8 pt-4 border-t border-zinc-100">
            {voted ? <p className="text-sm text-emerald-600 font-medium" data-testid="portal-kb-voted">Thanks for your feedback!</p> : (
              <div className="flex items-center gap-3">
                <span className="text-sm text-zinc-500">Was this helpful?</span>
                <button onClick={() => vote(true)} data-testid="portal-kb-vote-yes" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-emerald-600 border border-zinc-200 rounded-md px-3 py-1.5 hover:border-emerald-300"><ThumbsUp size={14} /> Yes</button>
                <button onClick={() => vote(false)} data-testid="portal-kb-vote-no" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-red-600 border border-zinc-200 rounded-md px-3 py-1.5 hover:border-red-300"><ThumbsDown size={14} /> No</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Group card — renders one of three admin-selectable layouts:
//  • image_cards (default): large JSM-style cover-image cards
//  • compact: small icon tile + name + count, denser grid
//  • list: horizontal row with icon, name, description and count
function PortalGroupCard({ group, slug, theme, layout = 'image_cards' }) {
  const to = `/help/${slug}/group/${group.group_id}`;
  const testid = `portal-group-card-${group.group_id}`;
  // 'contain' (default) shows the whole logo on a light backdrop; 'cover' crops to fill.
  const contain = (group.image_fit || 'contain') === 'contain';
  const tileBg = group.image_url
    ? (contain ? 'bg-contain bg-no-repeat bg-center bg-zinc-50' : 'bg-cover bg-center')
    : 'bg-cover bg-center';
  const tileStyle = group.image_url
    ? { backgroundImage: `url('${group.image_url}')` }
    : { background: `linear-gradient(135deg, ${theme}, ${theme}cc)` };

  if (layout === 'list') {
    return (
      <Link to={to} data-testid={testid}
        className="group flex items-center gap-4 bg-white border border-zinc-200 rounded-xl p-4 hover:shadow-md hover:border-zinc-300 transition-all duration-200">
        <div className={`w-12 h-12 rounded-lg ${tileBg} flex items-center justify-center shrink-0`} style={tileStyle}>
          {!group.image_url && <FolderSimple size={22} weight="duotone" className="text-white/90" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-zinc-900 text-sm truncate">{group.name}</p>
          {group.description && <p className="text-xs text-zinc-500 truncate">{group.description}</p>}
        </div>
        <span className="text-[11px] text-zinc-400 shrink-0">{group.count} type{group.count === 1 ? '' : 's'}</span>
        <CaretRight size={16} className="text-zinc-300 group-hover:text-zinc-900 transition-colors shrink-0" />
      </Link>
    );
  }

  if (layout === 'compact') {
    return (
      <Link to={to} data-testid={testid}
        className="group flex flex-col items-start gap-3 bg-white border border-zinc-200 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 hover:border-zinc-300 transition-all duration-200">
        <div className={`w-11 h-11 rounded-lg ${tileBg} flex items-center justify-center`} style={tileStyle}>
          {!group.image_url && <FolderSimple size={22} weight="duotone" className="text-white/90" />}
        </div>
        <div className="min-w-0 w-full">
          <p className="font-semibold text-zinc-900 text-sm truncate">{group.name}</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">{group.count} request type{group.count === 1 ? '' : 's'}</p>
        </div>
      </Link>
    );
  }

  // image_cards (default)
  return (
    <Link to={to} data-testid={testid}
      className="group block bg-white border border-zinc-200 rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
      <div className={`h-36 w-full ${tileBg} flex items-center justify-center p-3`} style={tileStyle}>
        {!group.image_url && <FolderSimple size={42} weight="duotone" className="text-white/90" />}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-zinc-900 text-base">{group.name}</p>
          <CaretRight size={16} className="text-zinc-400 group-hover:text-zinc-900 transition-colors flex-shrink-0" />
        </div>
        {group.description && <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{group.description}</p>}
        <p className="text-[11px] text-zinc-400 mt-2">
          {group.count} request type{group.count === 1 ? '' : 's'}
        </p>
      </div>
    </Link>
  );
}
